import {mediaUrl} from './paths';
import {studioText} from './studioText';
import type {Job, JobView, Preview, PreviewReel, Reel} from './types';

/** One reel of the current render, as `media.reels` lists it. */
export interface MediaReel {
  id: string;
  title?: string;
  startSec?: number;
  endSec?: number;
  video?: string;
  captions?: string;
}

/** Per-reel files of the current render, keyed by reel id. */
export function mediaReels(view: JobView): Record<string, MediaReel> {
  const reels: Record<string, MediaReel> = {};
  for (const reel of (view.media?.reels || []) as MediaReel[])
    if (reel?.id) reels[reel.id] = reel;
  // Older responses list only the joined video, which is all of a single reel.
  const only = view.job.storyboard?.reels;
  if (!Object.keys(reels).length && view.media?.video && only?.length === 1)
    reels[only[0].id] = {
      id: only[0].id,
      video: view.media.video,
      captions: view.media.captions,
    };
  return reels;
}

export const reelTitle = (reel: Reel, index: number) =>
  reel.title?.trim() || `Microreel ${index + 1}`;

export const reelSeconds = (reel: Reel) =>
  reel.scenes.reduce((total, scene) => total + (scene.durationS || 0), 0);

// The export holds the last scene 1.2 s after its narration where other scenes pause 0.4 s. A scene's
// planned duration has the shorter pause, so an unvoiced last scene adds the difference: the length
// shown is then the one the storyboard check and the export use. A clip has no hold: it ends where
// its range in the video does.
const END_HOLD_EXTRA_S = 0.8;

/** The reel's length as authors see it: planned until its scenes are voiced, then as measured. */
export const plannedReelSeconds = (reel: Reel) => {
  const last = reel.scenes[reel.scenes.length - 1];
  return (
    reelSeconds(reel) +
    (last?.durationS && !last.voiceAsset && last.layout !== 'clip'
      ? END_HOLD_EXTRA_S
      : 0)
  );
};

export function formatDuration(seconds: number) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** What the reel covers: its opening line. */
export const reelDescription = (reel: Reel) =>
  reel.scenes[0]?.narration || reel.scenes[0]?.onScreen || '';

/** The rendered reel's poster, else its first scene image. */
export function reelThumbnail(view: JobView, reel: Reel) {
  const video = mediaReels(view)[reel.id]?.video;
  // The renderer writes poster.jpg next to each reel.mp4.
  if (video && /\/reel\.mp4(\?|$)/.test(video))
    return mediaUrl(video.replace(/\/reel\.mp4(?=\?|$)/, '/poster.jpg'));
  const scene = reel.scenes.find(s => s.imageAsset);
  return scene
    ? mediaUrl(`jobs/${view.job.id}/media/assets/images/${scene.imageAsset}`)
    : undefined;
}

// Mirrors StoryboardValidator.IsUnresolvedSmeWarning: these notes block rendering.
export const unresolvedSmeNotes = (reel: Reel) =>
  (reel.warnings || []).filter(
    (w: string) => /needs sme/i.test(w) && !/^\s*resolved:/i.test(w),
  );

/** Someone has to confirm part of the reel before it can be rendered. */
export const needsConfirmation = (reel: Reel) =>
  reel.status === 'needsSme' ||
  reel.status === 'needs_sme' ||
  !!reel.needsSme ||
  unresolvedSmeNotes(reel).length > 0;

/** A render blocker as authors read it, with the microreel and scene it is about. */
export interface RenderBlocker {
  reelId?: string;
  sceneId?: string;
  /** "Microreel 3 (Using a fire extinguisher), scene 2"; none when the blocker names no microreel. */
  label?: string;
  message: string;
}

// StoryboardValidator.CanRender's reason for a microreel waiting for confirmation.
const CONFIRM_BLOCKER =
  /^Please confirm the flagged item at the top of the page\.?$/i;

/**
 * The server names the microreel and scene of a blocker by id ("r3/r3-s2: The number …"). Authors
 * know them by their place and title, and the flagged item is at the top of that microreel's own
 * storyboard, which may not be the one open. `sceneNoun` names a part of the microreel: the
 * localised scene, or clip in a microreel cut from a video (`studioText.sceneNoun` / `clipNoun`).
 */
export function renderBlocker(
  reels: Reel[],
  reason: string,
  sceneNoun: string,
): RenderBlocker {
  const [, reelId, sceneId, message] =
    /^([^\s/:]+)(?:\/([^\s:]+))?: ([\s\S]+)$/.exec(reason) || [];
  const index = reelId ? reels.findIndex(reel => reel.id === reelId) : -1;
  if (index < 0) return {message: reason};
  const reel = reels[index];
  const title = reel.title?.trim();
  const scene = sceneId ? reel.scenes.findIndex(s => s.id === sceneId) : -1;
  return {
    reelId,
    sceneId: scene < 0 ? undefined : sceneId,
    label:
      scene < 0
        ? studioText.blockerLabel(index + 1, title)
        : studioText.blockerLabel(index + 1, title, sceneNoun, scene + 1),
    message: CONFIRM_BLOCKER.test(message)
      ? studioText.confirmFlaggedBlocker
      : message,
  };
}

/** The blocker on one line, as a tooltip shows it. */
export const renderBlockerText = ({label, message}: RenderBlocker) =>
  label ? `${label}: ${message}` : message;

/** How far the playable previews of the current storyboard are. */
export interface PreviewProgress {
  /** The preview of the current storyboard, once at least one microreel can play. */
  preview: Preview | null;
  /** Each microreel's playable preview by reel id; missing until it is ready. */
  byReel: Record<string, PreviewReel>;
  ready: number;
  total: number;
  /** Every microreel can play. */
  allReady: boolean;
  /**
   * The server makes the previews one microreel at a time, so each one plays as soon as it
   * is ready. Older servers make them all at once and say none of this.
   */
  streaming: boolean;
  /** While streaming: the microreel being generated now, the first one not ready yet. */
  generatingId?: string;
}

// The server's stage while it generates one microreel of several, then the reel's title. A
// clipping job cuts them from its video instead.
const GENERATING_STAGE =
  /^(?:Generating|Cutting) microreel (\d+) of (\d+)(?::|$)/;

/**
 * Which microreels can play yet. Newer servers list only the microreels whose preview is
 * ready and stamp each one; older ones list all of them at once when the last is ready.
 */
export function previewProgress(view: JobView): PreviewProgress {
  const {job} = view;
  const reels = job.storyboard?.reels || [];
  const preview =
    view.preview?.versionHash === job.storyboard?.versionHash &&
    view.preview?.reels?.length
      ? view.preview
      : null;
  const byReel: Record<string, PreviewReel> = {};
  for (const reel of preview?.reels || []) byReel[reel.id] = reel;
  // Older servers list every microreel at once; only then can the position stand in for the id.
  if (preview?.reels.length === reels.length)
    reels.forEach((reel, index) => {
      if (!byReel[reel.id]) byReel[reel.id] = preview.reels[index];
    });
  const ready = reels.filter(reel => byReel[reel.id]).length;
  const stage = GENERATING_STAGE.exec(job.stage?.trim() || '');
  const streaming =
    !!stage ||
    (!!preview && ready < reels.length) ||
    reels.some(reel => reel.previewReadyAt || reel.previewRuntimeVersion) ||
    (preview?.reels || []).some(reel => reel.readyAt || reel.runtimeVersion);
  const named =
    stage && Number(stage[2]) === reels.length
      ? reels[Number(stage[1]) - 1]
      : undefined;
  const generating = streaming
    ? named && !byReel[named.id]
      ? named
      : reels.find(reel => !byReel[reel.id])
    : undefined;
  return {
    preview,
    byReel,
    ready,
    total: reels.length,
    allReady: !!preview && ready === reels.length,
    streaming,
    generatingId: generating?.id,
  };
}

/** A render that stopped before it made every microreel: which ones it made, and which not. */
export interface PartialRender {
  /** The microreels it made, in storyboard order. */
  made: Reel[];
  /**
   * The ones it did not make, or that changed since, in the order generating makes them (see
   * reelsToMake); in storyboard order when the server makes every microreel again.
   */
  missing: Reel[];
  /** The missing ones a render made before and that changed since; the others were never made. */
  changed: Reel[];
  /**
   * Generating again keeps the ones made and makes only the missing ones. Newer servers say
   * which they keep (`reusableReels`), even once an edit hides their previews; older ones make
   * every microreel again.
   */
  continues: boolean;
}

/**
 * The microreels generating makes, in the order it makes them. Newer servers keep the ones in
 * `reusableReels` and make the others: first the ones no render made, then the ones changed
 * since (a render that made one stamped its `preparedContentHash`). Older servers make every
 * microreel, in storyboard order.
 */
export function reelsToMake(view: JobView): Reel[] {
  const reels = view.job.storyboard?.reels || [];
  const kept = view.reusableReels;
  if (!Array.isArray(kept)) return reels;
  const making = reels.filter(reel => !kept.includes(reel.id));
  return [
    ...making.filter(reel => !reel.preparedContentHash),
    ...making.filter(reel => !!reel.preparedContentHash),
  ];
}

/** The microreel a new render plays first: one it keeps plays at once, else the first it makes. */
export const firstToPlay = (view: JobView): Reel | undefined =>
  (view.job.storyboard?.reels || []).find(
    reel => view.reusableReels?.includes(reel.id),
  ) ?? reelsToMake(view)[0];

/** The microreels split into `done` and `missing`, when some of each. */
function splitReels(
  done: Reel[],
  missing: Reel[],
  continues: boolean,
): PartialRender | null {
  return done.length && missing.length
    ? {
        made: done,
        missing,
        changed: missing.filter(reel => !!reel.preparedContentHash),
        continues,
      }
    : null;
}

/**
 * Whether a render stopped before it finished, until the next one starts: a failed one, or a
 * storyboard edited since. A render that finished exported every microreel, so one changed
 * since is made again, not finished; a first one that stopped while joining them exported them
 * all too, but has not finished (no render counted).
 */
function unfinished(job: Job): boolean {
  if (job.status === 'failed') return true;
  if (job.status !== 'ready_for_review') return false;
  const reels = job.storyboard?.reels || [];
  return !((job.renderCount ?? 0) > 0 && reels.every(reel => reel.renderedAt));
}

/**
 * A render that stopped part way, until the next one starts. Newer servers name the microreels
 * generating again keeps. Without that list, a failed render's previews show which ones it
 * made: older servers make every microreel again, and an edit hides those previews.
 */
export function partialRender(view: JobView): PartialRender | null {
  const {job} = view;
  const reels = job.storyboard?.reels || [];
  if (!unfinished(job)) return null;
  const kept = view.reusableReels;
  if (Array.isArray(kept)) {
    const partial = splitReels(
      reels.filter(reel => kept.includes(reel.id)),
      reelsToMake(view),
      true,
    );
    if (partial) return partial;
  }
  if (job.status !== 'failed') return null;
  const {byReel} = previewProgress(view);
  return splitReels(
    reels.filter(reel => byReel[reel.id]),
    reels.filter(reel => !byReel[reel.id]),
    false,
  );
}

/**
 * A render that stopped after it made every microreel, before it finished their files:
 * generating keeps them all and only finishes. Only newer servers say so (`reusableReels`).
 */
export function keepsEveryReel(view: JobView): boolean {
  const reels = view.job.storyboard?.reels || [];
  const kept = view.reusableReels;
  return (
    unfinished(view.job) &&
    Array.isArray(kept) &&
    reels.length > 0 &&
    reels.every(reel => kept.includes(reel.id))
  );
}

/**
 * After a render that stopped part way, the microreels no render made, when generating keeps
 * none of the ones it made, e.g. once the author edited them all (partialRender covers one that
 * keeps some, or whose previews still show what it made). Generating makes these first, then
 * the others (see reelsToMake). Only newer servers say which ones a render made
 * (`preparedContentHash`); with none made, there is nothing to tell apart.
 */
export function unmadeReels(view: JobView): Reel[] {
  const reels = view.job.storyboard?.reels || [];
  const kept = view.reusableReels;
  if (
    !Array.isArray(kept) ||
    kept.length > 0 ||
    !unfinished(view.job) ||
    partialRender(view)
  )
    return [];
  const unmade = reels.filter(reel => !reel.preparedContentHash);
  return unmade.length < reels.length ? unmade : [];
}
